import { z } from 'zod'
import { PlanAndRateOverrideRuleStatus } from '../enums/plan-and-rate-override-rule.enum'
import type {
  PlanAndRateOverrideParams,
  PlanAndRateOverrideRuleResponseDto,
  CreatePlanAndRateOverrideRuleRequestDto,
} from '../../../api/dto/plan-and-rate-override-rules.dto'

export type { PlanAndRateOverrideParams }

export interface PlanAndRateViewMetadata {
  status: PlanAndRateOverrideRuleStatus
  createdAt: string
  createdByEmail: string | null
  updatedAt: string
  updatedByEmail: string | null
}

export const REQUIRED_PARAMS_FIELDS: ReadonlyArray<{
  key: keyof PlanAndRateOverrideParams
  label: string
}> = [
  { key: 'carrier', label: 'Carrier' },
  { key: 'planName', label: 'Plan Name' },
  { key: 'householdName', label: 'Carrier Friendly Name' },
  { key: 'planType', label: 'Plan Type' },
  { key: 'level', label: 'Metal Level' },
  { key: 'networkName', label: 'Network' },
  { key: 'serviceAreaId', label: 'Service Area ID' },
  { key: 'baseRate', label: 'Base Rate' },
]

const toNumber = (v: unknown) =>
  v === '' || v === null || v === undefined ? undefined : Number(v)

const paramsSchema = z
  .object({
    carrier: z.string().nullable().optional(),
    planName: z.string().nullable().optional(),
    householdName: z.string().nullable().optional(),
    planType: z.string().nullable().optional(),
    level: z.string().nullable().optional(),
    hsa: z.boolean().nullable().optional(),
    networkName: z.string().nullable().optional(),
    onExchange: z.boolean().nullable().optional(),
    offExchange: z.boolean().nullable().optional(),
    isForSale: z.boolean().nullable().optional(),
    proofOfResidenceRequired: z.boolean().nullable().optional(),
    serviceAreaId: z.string().nullable().optional(),
    baseRate: z.number().nullable().optional(),
    planFee: z.number().nullable().optional(),
    planStatus: z.string().nullable().optional(),
    crosswalkPlanId: z.string().nullable().optional(),
    rateAdjustments: z.record(z.number().nullable()).optional(),
  })
  .superRefine((data, ctx) => {
    for (const { key, label } of REQUIRED_PARAMS_FIELDS) {
      const val = data[key]
      const isEmpty =
        val === null ||
        val === undefined ||
        (typeof val === 'string' && val.trim() === '')
      if (isEmpty) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label} is required`,
          path: [key],
        })
      }
    }
  })

export const PlanAndRateOverrideFormSchema = z.object({
  year: z.preprocess(
    toNumber,
    z
      .number({
        required_error: 'Year is required',
        invalid_type_error: 'Year is required',
      })
      .int()
      .min(2020, 'Year must be 2020 or later')
      .max(2035, 'Year must be 2035 or earlier')
  ),
  state: z.string().min(1, 'State is required'),
  hiosPlanId: z.string().min(1, 'HIOS Plan ID is required'),
  params: paramsSchema.optional(),
})

export type PlanAndRateOverrideFormValues = z.infer<
  typeof PlanAndRateOverrideFormSchema
>

export interface PlanAndRateServerFieldError {
  field: string
  errors: string[]
}

export function flatToApiPayload(
  values: PlanAndRateOverrideFormValues
): CreatePlanAndRateOverrideRuleRequestDto {
  return {
    template: 'UPSERT_RULE' as const,
    match: {
      year: values.year,
      state: values.state,
      hiosPlanId: values.hiosPlanId,
    },
    params: values.params ?? {},
  }
}

export function apiPayloadToForm(
  rule: PlanAndRateOverrideRuleResponseDto
): PlanAndRateOverrideFormValues {
  return {
    year: rule.match.year,
    state: rule.match.state,
    hiosPlanId: rule.match.hiosPlanId,
    params: rule.params ?? {},
  }
}
